import React, { useState, useEffect } from 'react';
import axios from './utils/axiosConfig';
import { Card, Button, Table, Badge, Spinner, ProgressBar, Alert, Form } from 'react-bootstrap';
import { useAuth } from './context/AuthContext';

const CompanyCoordinateRepair = () => {
  const { user } = useAuth();
  const token = localStorage.getItem('token');
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [results, setResults] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    fetchMissingCompanies();
  }, []);

  const fetchMissingCompanies = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/companies/missing-coordinates', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setCompanies(response.data.companies || []);
    } catch (error) {
      console.error('獲取公司列表失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
    } else {
      setSelectedIds(companies.map(c => c.s_id));
    }
    setSelectAll(!selectAll);
  };

  const handleSelect = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleBatchRepair = async () => {
    if (selectedIds.length === 0) {
      alert('請選擇要修復的公司');
      return;
    }

    if (!window.confirm(`確定要修復 ${selectedIds.length} 家公司的座標嗎？`)) {
      return;
    }

    setProcessing(true);
    setProgress({ current: 0, total: selectedIds.length });
    setResults(null);

    try {
      const response = await axios.post('/companies/batch-geocode', {
        company_ids: selectedIds,
        delay: 300 // 300ms 延遲避免超過 API 限制
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setResults(response.data.results);
      
      // 重新載入列表
      await fetchMissingCompanies();
      setSelectedIds([]);
      setSelectAll(false);
    } catch (error) {
      console.error('批次修復失敗:', error);
      alert('批次修復失敗：' + (error.response?.data?.message || error.message));
    } finally {
      setProcessing(false);
    }
  };

  const handleSingleRepair = async (id) => {
    if (!window.confirm('確定要修復這家公司的座標嗎？')) {
      return;
    }

    setProcessing(true);
    try {
      const response = await axios.post(`/companies/${id}/geocode`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      alert('座標修復成功！');
      await fetchMissingCompanies();
    } catch (error) {
      console.error('修復失敗:', error);
      alert('修復失敗：' + (error.response?.data?.message || error.message));
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" />
        <p className="mt-2">載入中...</p>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4">
      <Card>
        <Card.Header>
          <h4 className="mb-0">🔧 智能座標修復系統</h4>
          <small className="text-muted">為有地址但缺少座標的公司自動獲取經緯度</small>
        </Card.Header>

        <Card.Body>
          {processing && (
            <Alert variant="info">
              <Spinner animation="border" size="sm" className="me-2" />
              正在處理中，請稍候...
              {progress.total > 0 && (
                <ProgressBar
                  now={(progress.current / progress.total) * 100}
                  label={`${progress.current} / ${progress.total}`}
                  className="mt-2"
                />
              )}
            </Alert>
          )}

          {results && (
            <Alert variant={results.success > 0 ? 'success' : 'warning'} dismissible onClose={() => setResults(null)}>
              <h6>批次處理完成</h6>
              <ul className="mb-0">
                <li>總計：{results.total} 筆</li>
                <li className="text-success">成功：{results.success} 筆</li>
                <li className="text-danger">失敗：{results.failed} 筆</li>
              </ul>
            </Alert>
          )}

          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <Badge bg="warning">{companies.length} 家公司缺少座標</Badge>
              {selectedIds.length > 0 && (
                <Badge bg="primary" className="ms-2">{selectedIds.length} 筆已選擇</Badge>
              )}
            </div>
            <div>
              <Button
                variant="success"
                onClick={handleBatchRepair}
                disabled={processing || selectedIds.length === 0}
              >
                🔧 批次修復 ({selectedIds.length})
              </Button>
            </div>
          </div>

          {companies.length === 0 ? (
            <Alert variant="success">
              <h5>✅ 太好了！</h5>
              <p className="mb-0">所有公司都已經有座標了</p>
            </Alert>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th width="5%">
                    <Form.Check
                      type="checkbox"
                      checked={selectAll}
                      onChange={handleSelectAll}
                      disabled={processing}
                    />
                  </th>
                  <th width="10%">ID</th>
                  <th width="25%">公司名稱</th>
                  <th width="40%">地址</th>
                  <th width="10%">狀態</th>
                  <th width="10%">操作</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((company) => (
                  <tr key={company.s_id}>
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={selectedIds.includes(company.s_id)}
                        onChange={() => handleSelect(company.s_id)}
                        disabled={processing}
                      />
                    </td>
                    <td>{company.s_id}</td>
                    <td><strong>{company.s_com_name}</strong></td>
                    <td>{company.s_address || <span className="text-danger">無地址</span>}</td>
                    <td>
                      {company.geocode_status === 'failed' && (
                        <Badge bg="danger">失敗</Badge>
                      )}
                      {company.geocode_status === 'pending' && (
                        <Badge bg="warning">待處理</Badge>
                      )}
                      {!company.s_address && (
                        <Badge bg="secondary">無地址</Badge>
                      )}
                    </td>
                    <td>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleSingleRepair(company.s_id)}
                        disabled={processing || !company.s_address}
                      >
                        修復
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default CompanyCoordinateRepair;
